/**
 * The lead's pipeline stage, moved from the call it was moved by.
 *
 * A cold call is the event that changes where a lead stands, and until now the
 * operator had to hang up, leave the call, find the lead on the board and drag
 * it. In practice that meant the board went stale and the pipeline stopped
 * describing anything.
 *
 * Its own file rather than more lines in `CallLinks`: attaching a call to a
 * company and advancing that company through the funnel are two decisions, and
 * only one of them is about this call.
 *
 * NOT routed through the call's PATCH. That endpoint owns CallLog columns and is
 * gated on `use:crm.calls`; a lead's status is another module's row behind
 * `manage:crm.clients`, which is why the control is hidden without that key
 * rather than failing with a 403 after the click.
 */
import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { updateLead, type LeadStatus } from "../../../lib/clients";
import { STATUSES } from "../leads/shared";

export function CallLeadStatus({
  leadId,
  status,
  onMoved,
}: {
  leadId: string;
  status: string;
  /** So the pane's copy of the call carries the new stage without a refetch. */
  onMoved: (next: LeadStatus) => void;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(status);

  async function move(next: string) {
    if (next === shown) return;
    const previous = shown;
    setShown(next);
    setBusy(true);
    try {
      await updateLead(leadId, { status: next as LeadStatus });
      onMoved(next as LeadStatus);
    } catch {
      // The board is the truth and this control is a shortcut to it. A failed
      // write that left the pill showing the new stage would be a lie an
      // operator acts on.
      setShown(previous);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Select value={shown} onValueChange={(v) => void move(v)} disabled={busy}>
      <SelectTrigger className="h-7 w-[150px] text-xs" aria-label={t("calls.detail.leadStatus")}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STATUSES.map((s) => (
          <SelectItem key={s.key} value={s.key} className="text-xs">
            {t(`leads.status.${s.key}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
